import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { DashboardWidgetId, isDashboardWidgetId } from '../config/dashboardWidgets';

interface PointerLike {
  pointerId: number;
  clientX: number;
  clientY: number;
  preventDefault: () => void;
  currentTarget: {
    setPointerCapture: (pointerId: number) => void;
    releasePointerCapture: (pointerId: number) => void;
  };
}

interface DashboardWidgetDragOverlayProps {
  readonly widgetId: DashboardWidgetId;
  readonly enabled: boolean;
  readonly isDragging: boolean;
  readonly onPointerDown: (widgetId: DashboardWidgetId, event: PointerLike) => void;
  readonly onPointerMove: (event: PointerLike) => void;
  readonly onPointerUp: (event: PointerLike) => void;
  readonly children: React.ReactNode;
}

export const WIDGET_SLOT_ATTR = 'data-dashboard-widget-id';

export const widgetIdFromPoint = (clientX: number, clientY: number): DashboardWidgetId | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const node = document.elementFromPoint(clientX, clientY);
  let current: Element | null = node;
  while (current) {
    const value = current.getAttribute(WIDGET_SLOT_ATTR);
    if (value && isDashboardWidgetId(value)) {
      return value;
    }
    current = current.parentElement;
  }

  return null;
};

export const DashboardWidgetDragOverlay: React.FC<DashboardWidgetDragOverlayProps> = ({
  widgetId,
  enabled,
  isDragging,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  children,
}) => {
  if (!enabled || Platform.OS !== 'web') {
    return <>{children}</>;
  }

  const overlay = React.createElement('div', {
    [WIDGET_SLOT_ATTR]: widgetId,
    onPointerDown: (event: PointerLike) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      onPointerDown(widgetId, event);
    },
    onPointerMove: (event: PointerLike) => {
      onPointerMove(event);
    },
    onPointerUp: (event: PointerLike) => {
      event.currentTarget.releasePointerCapture(event.pointerId);
      onPointerUp(event);
    },
    onPointerCancel: (event: PointerLike) => {
      event.currentTarget.releasePointerCapture(event.pointerId);
      onPointerUp(event);
    },
    style: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      cursor: isDragging ? 'grabbing' : 'grab',
      zIndex: 1,
      userSelect: 'none',
      touchAction: 'none',
    },
  });

  return (
    <View style={styles.body}>
      {children}
      {overlay}
    </View>
  );
};

const styles = StyleSheet.create({
  body: {
    position: 'relative',
    minHeight: 140,
  },
});
